<template>
  <main
    class="min-h-screen bg-[linear-gradient(135deg,#fdfcfb_0%,#e2d1c3_100%)] bg-fixed font-['Montserrat'] pb-6"
  >
    <div v-if="loadingPage" class="relative min-h-screen w-full flex justify-center items-center">
      <svg width="300px" height="200px" viewBox="0 0 187.3 93.7" preserveAspectRatio="xMidYMid meet">
        <path stroke="#CD0056" id="outline" fill="none" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" stroke-miterlimit="10" d="M93.9,46.4c9.3,9.5,13.8,17.9,23.5,17.9s17.5-7.8,17.5-17.5s-7.8-17.6-17.5-17.5c-9.7,0.1-13.3,7.2-22.1,17.1 				c-8.9,8.8-15.7,17.9-25.4,17.9s-17.5-7.8-17.5-17.5s7.8-17.5,17.5-17.5S86.2,38.6,93.9,46.4z" />
        <path id="outline-bg" opacity="0.05" fill="none" stroke="#CD0056" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" stroke-miterlimit="10" d="				M93.9,46.4c9.3,9.5,13.8,17.9,23.5,17.9s17.5-7.8,17.5-17.5s-7.8-17.6-17.5-17.5c-9.7,0.1-13.3,7.2-22.1,17.1 				c-8.9,8.8-15.7,17.9-25.4,17.9s-17.5-7.8-17.5-17.5s7.8-17.5,17.5-17.5S86.2,38.6,93.9,46.4z" />
      </svg>
    </div>

    <div v-else class="container mx-auto max-w-[960px] p-4 md:px-0">
      <div class="flex justify-between items-center mb-4">
        <a href="/" class="mx-auto">
          <img
            src="@assets/images/logo.svg"
            alt="Logo Memoorias"
            class="my-2 mx-auto"
            height="20"
            width="178"
          />
        </a>
      </div>

      <instructions-step
        v-if="!instructionsSeen"
        v-model="instructionsSeen"
      />

      <interview-service-step
        v-if="instructionsSeen && quotation.modality !== null"
        :step-number="stepNumbers.interviewService"
        v-model="quotation.interviewServiceId"
        :quotation="quotation"
        :interview-services="interviewServices"
      />

      <protagonist-info-step
        v-if="quotation.interviewServiceId !== null"
        :step-number="stepNumbers.protagonistInfo"
        v-model="quotation"
        :handleProtagonistInfoCompleted="() => (protagonistCompleted = true)"
      />

      <additional-info-step
        v-if="protagonistCompleted"
        :step-number="stepNumbers.additionalInfo"
        v-model="quotation"
        :handleAdditionalInfoCompleted="() => (additionalInfoCompleted = true)"
      />

      <template v-if="additionalInfoCompleted">
        <contact-step
          v-if="!isAuthenticated"
          :step-number="stepNumbers.contact"
          v-model="quotation"
          @track-quotation="trackFormSubmission"
        />
      </template>

      <included-services
        v-if="
          (quotation.interviewServiceId !== null && isAuthenticated) ||
          additionalInfoCompleted
        "
      />
    </div>

    <price-summary
      v-if="quotation.interviewServiceId !== null && !hidePrice"
      :quotation="quotation"
    />

    <!-- Progress Bar -->
    <div class="p-4 fixed bottom-0 left-0 right-0 bg-white shadow-md">
      <div class="max-w-4xl mx-auto">
        <div class="w-full bg-gray-200 rounded-full h-2">
          <div
            class="bg-gradient-to-r from-pink-500 to-red-600 h-2 rounded-full transition-all duration-500 ease-out"
            :style="{ width: progressPercentage + '%' }"
          ></div>
        </div>
        <div class="flex justify-between mt-2  mx-auto">
          <span class="text-xs text-gray-500">{{ currentStepName }}</span>
          <span class="text-xs text-gray-500">{{ completedSteps }}/{{ totalSteps }}</span>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import MotiveStep from "./components/steps/MotiveStep.vue";
import InstructionsStep from "./components/steps/InstructionsStep.vue";
import ProtagonistInfoStep from "./components/steps/ProtagonistInfoStep.vue";
import IntervieweeStep from "./components/steps/IntervieweeStep.vue";
import FormatStep from "./components/steps/FormatStep.vue";
import ModalityStep from "./components/steps/ModalityStep.vue";
import InterviewServiceStep from "./components/steps/InterviewServiceStep.vue";
import ContactStep from "./components/steps/ContactStep.vue";
import IncludedServices from "./components/steps/IncludedServices.vue";
import AdditionalInfoStep from "./components/steps/AdditionalInfoStep.vue";
import PriceSummary from "./components/PriceSummary.vue";
import trackingService from "./services/trackingService.js";

export default {
  props: [
    "motives",
    "interviewServices",
    "selectedMotive",
    "hidePrice",
    "authenticated",
    "loginPath",
  ],

  components: {
    MotiveStep,
    ProtagonistInfoStep,
    IntervieweeStep,
    FormatStep,
    ModalityStep,
    InterviewServiceStep,
    ContactStep,
    InstructionsStep,
    IncludedServices,
    AdditionalInfoStep,
    PriceSummary,
  },

  data() {
    return {
      loadingPage: true,
      disabledSteps: [],
      stepNumbers: {
        motive: '1',
        modality: '2',
        interviewService: '3',
        protagonistInfo: '4',
        additionalInfo: '5',
        contact: '6',
      },
      quotation: {
        motive: null,
        protagonistInterviewee: true,
        protagonistFirstName: null,
        protagonistLastName: null,
        protagonistNickname: null,
        format: "video",
        modality: null,
        interviewServiceId: null,
        contactFirstName: null,
        contactLastName: null,
        contactCellphone: null,
        contactEmail: null,
        additionalInfo: null,
      },
      instructionsSeen: false,
      protagonistCompleted: false,
      additionalInfoCompleted: false,
      isAuthenticated: this.authenticated === "true" ?? false,
    };
  },

  provide() {
    return {
      isPriceHidden: this.hidePrice === "true",
      isAuthenticated: this.authenticated === "true" ?? false,
      loginPath: this.loginPath,
    };
  },

  watch: {
    // Track motive step view when motive is selected
    'quotation.motive'(newMotive) {
      if (newMotive !== null) {
        this.trackStepCompleted('motive-step', { selectedMotive: newMotive });
      }
    },

    // Track instructions step view
    instructionsSeen(newValue) {
      if (newValue) {
        this.trackStepView('instructions-step', true);
      }
    },

    // Track modality step view when modality is selected
    'quotation.modality'(newModality) {
      if (newModality !== null) {
        this.trackStepCompleted('modality-step', { selectedModality: newModality });
      }
    },

    // Track interview service step view when service is selected
    'quotation.interviewServiceId'(newServiceId) {
      if (newServiceId !== null) {
        this.trackStepCompleted('interview-service-step', { selectedServiceId: newServiceId });
      }
    },

    // Track protagonist info completion
    protagonistCompleted(newValue) {
      if (newValue) {
        this.trackStepCompleted('protagonist-info-step');
      }
    },

    // Track additional info completion
    additionalInfoCompleted(newValue) {
      if (newValue) {
        this.trackStepCompleted('additional-info-step');
      }
    }
  },

  mounted() {
    this.defineFlow();

    // If the user is coming from the landing page with a type of interviewee selected
    if (!this.selectedMotive) return;

    if (this.motives.map((type) => type.value).includes(this.selectedMotive)) {
      this.quotation.motive = this.selectedMotive;
    }
  },

  computed: {
    stepIsDisabled() {
      return (step) => {
        return this.disabledSteps.includes(step);
      }
    },

    progressPercentage() {
      return Math.round((this.completedSteps / this.totalSteps) * 100);
    },

    totalSteps() {
      return this.isAuthenticated ? 4 : 5; // 5 steps for authenticated users, 6 for guests
    },

    completedSteps() {
      let steps = 0;

      // Step 1: Instructions seen
      if (this.instructionsSeen) steps++;

      // Step 2: Interview service selected
      if (this.quotation.interviewServiceId !== null) steps++;

      // Step 3: Protagonist info completed
      if (this.protagonistCompleted) steps++;

      // Step 4: Additional info completed (only for guests)
      if (!this.isAuthenticated && this.additionalInfoCompleted) steps++;

      return steps;
    },

    currentStepName() {
      if (!this.instructionsSeen) return 'Instrucciones';
      if (this.quotation.interviewServiceId === null) return 'Capítulos';
      if (!this.protagonistCompleted) return 'Información del protagonista';
      if (!this.isAuthenticated && !this.additionalInfoCompleted) return 'Información adicional';
      if (!this.isAuthenticated) return 'Información de contacto';
      return 'Resumen de servicios';
    },
  },

  methods: {
    trackStepView(stepName, isVisible, additionalData = {}) {
      if (!isVisible) {
        return;
      }

      trackingService.trackStepView(stepName, this.quotation, additionalData);
    },

    trackStepCompleted(stepName, additionalData = {}) {
      trackingService.trackStepCompleted(stepName, this.quotation, additionalData);
    },

    trackFormSubmission(quotationData = {}) {
      trackingService.trackFormSubmission(quotationData);
    },

    defineFlow() {
      trackingService.trackPageViewed();

      this.disabledSteps = [
        'motive-step',
        'modality-step',
      ];

      if (this.selectedMotive && this.motives.find((type) => type.value === this.selectedMotive)) {
        this.quotation.motive = this.selectedMotive;
      } else {
        this.quotation.motive = this.motives[this.motives.length - 1].value;
      }

      this.quotation.modality = 'study';
      this.stepNumbers.interviewService = '1';
      this.stepNumbers.protagonistInfo = '2';
      this.stepNumbers.additionalInfo = '3';
      this.stepNumbers.contact = '4';

      this.loadingPage = false;
    }
  }
};
</script>
